import { GripVertical, Plus, Search, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { useReactFlow } from 'reactflow';
import { useNodeValidation, useNodeValidationPattern } from '../../hooks/useNodeValidation';
import { getStandardizedVariables } from '../../lib/variable-display-utils';
import type { TransformConfig } from '../../types/nodes';
import { ProcessingNodeData } from '../../types/nodes';
import { ValidationResultDisplay } from '../shared/ValidationResultDisplay';

interface TransformConfigProps {
	nodeId: string;
	data: ProcessingNodeData;
	onChange: (data: ProcessingNodeData) => void;
	onWizardStateChange?: React.Dispatch<
		React.SetStateAction<{
			wizardMode: boolean;
			currentStep: number;
			totalSteps: number;
			canProceedToNext: boolean;
			onPreviousStep: () => void;
			onNextStep: () => void;
			onCompleteWizard: () => Promise<void>;
			validationAttempted: boolean;
			validationFailed: boolean;
		}>
	>;
	onValidationRequest?: (validate: () => Promise<boolean>) => void;
}

interface Transformation {
	id: string;
	output: string;
	expression: string;
	condition?: string;
	description?: string;
}

interface ContextToken {
	id: string;
	label: string;
	type: string;
	description?: string;
	source: 'form' | 'transform' | 'upstream' | 'start';
}

const defaultConfig: TransformConfig = {
	transformations: [
		{
			id: crypto.randomUUID(),
			output: '',
			expression: '',
			description: '',
		},
	],
	onError: 'fail',
	preserveOriginal: true,
	storeAs: '', // Default to empty, will use node ID
};

export function TransformConfig({
	nodeId,
	data,
	onChange,
	onWizardStateChange,
	onValidationRequest,
}: TransformConfigProps) {
	const { validateNode, isValidating, lastValidationResult, clearValidationResult } =
		useNodeValidation();

	// Use the validation pattern hook for consistent behavior
	const {
		validationAttempted,
		validationFailed,
		performValidation,
		setValidationAttempted,
		setValidationFailed,
	} = useNodeValidationPattern(nodeId, data);

	const { getNodes, getEdges } = useReactFlow();
	const [config, setConfig] = useState<TransformConfig>(() => {
		const existingConfig = data.config as Partial<TransformConfig>;

		// Normalize transformations to ensure all required properties exist
		const normalizeTransformations = (transformations: any[]) => {
			return transformations.map(t => ({
				id: t?.id || crypto.randomUUID(),
				output: t?.output || '',
				expression: t?.expression || '',
				condition: t?.condition || '',
				description: t?.description || '',
			}));
		};

		return {
			...defaultConfig,
			...existingConfig,
			transformations:
				existingConfig?.transformations && existingConfig.transformations.length > 0
					? normalizeTransformations(existingConfig.transformations)
					: defaultConfig.transformations,
		};
	});

	// Wizard state management - always start in wizard mode for transform nodes
	const [wizardMode, setWizardMode] = useState(true);
	const [currentStep, setCurrentStep] = useState(1);

	// Step validation logic for wizard
	const canProceedToStep = (step: number) => {
		switch (step) {
			case 1:
				return true; // Setup - can always start
			case 2:
				return config.transformations.some(t => t?.output?.trim() !== ''); // Transformations - need at least one output
			case 3:
				return config.transformations.some(t => t?.expression?.trim() !== ''); // Test - need at least one expression
			default:
				return false;
		}
	};

	// Register validation function with parent
	useEffect(() => {
		if (onValidationRequest) {
			onValidationRequest(performValidation);
		}
	}, [onValidationRequest, performValidation]);

	// Communicate wizard state to parent component (BaseNode)
	useEffect(() => {
		// Debug logging to understand the issue
		console.log('🔍 TransformConfig - Wizard State Update:', {
			wizardMode,
			currentStep,
			validationAttempted,
			validationFailed,
			hasOnWizardStateChange: !!onWizardStateChange,
			configTransformations: config.transformations.length,
			hasValidTransformations: config.transformations.some(
				t =>
					t.output && t.output.trim() !== '' && t.expression && t.expression.trim() !== ''
			),
		});

		if (onWizardStateChange) {
			onWizardStateChange({
				wizardMode,
				currentStep,
				totalSteps: 3, // Transform wizard has 3 steps
				canProceedToNext: canProceedToStep(currentStep + 1),
				onPreviousStep: () => setCurrentStep(Math.max(1, currentStep - 1)),
				onNextStep: () => setCurrentStep(currentStep + 1),
				onCompleteWizard: async () => {
					console.log('🔍 TransformConfig.onCompleteWizard - Starting wizard completion');
					// Step 1: If validation hasn't been attempted yet, run validation and stay in node
					if (!validationAttempted) {
						console.log(
							'🔍 TransformConfig.onCompleteWizard - First attempt, running validation'
						);
						try {
							const result = await validateNode(nodeId, data);
							setValidationAttempted(true);

							if (!result.valid) {
								// Validation failed - stay in node and show errors
								setValidationFailed(true);
								console.log(
									'🔍 TransformConfig.onCompleteWizard - Validation failed, staying in node:',
									result
								);
								return; // Don't exit the node
							} else {
								// Validation succeeded - proceed to exit
								setValidationFailed(false);
								console.log(
									'🔍 TransformConfig.onCompleteWizard - Validation succeeded:',
									result
								);
							}
						} catch (error) {
							console.error('Validation failed:', error);
							setValidationAttempted(true);
							setValidationFailed(true);
							return; // Don't exit the node on error
						}
					}

					console.log(
						'🔍 TransformConfig.onCompleteWizard - Second attempt or validation passed, exiting node'
					);
					// Step 2: If validation was already attempted (regardless of success/failure), exit the node
					// This allows users to force exit even if validation failed

					// Save the current configuration
					const finalConfig = {
						...data,
						config: {
							...config,
							// Clean up any invalid transformations before saving
							transformations: config.transformations.filter(
								t =>
									t.output &&
									t.output.trim() !== '' &&
									t.expression &&
									t.expression.trim() !== ''
							),
						},
					};
					onChange(finalConfig);

					// Reset validation state for next time
					setValidationAttempted(false);
					setValidationFailed(false);

					// Exit wizard mode
					setWizardMode(false);
				},
				validationAttempted,
				validationFailed,
			});
		}
	}, [
		wizardMode,
		currentStep,
		config,
		onWizardStateChange,
		validationAttempted,
		validationFailed,
		validateNode,
		nodeId,
		data,
		onChange,
	]);

	// Convert transformations to internal format with unique IDs
	const [transformations, setTransformations] = useState<Transformation[]>(() => {
		return config.transformations.map((t, index) => ({
			...t,
			id: `transform-${index}-${Date.now()}`,
		}));
	});

	const [showDefaultValues, setShowDefaultValues] = useState(config.onError === 'default');
	const [showContextPanel, setShowContextPanel] = useState(false);
	const [contextSearchTerm, setContextSearchTerm] = useState('');
	const [activeTokenDropdown, setActiveTokenDropdown] = useState<string | null>(null);

	// Get available context tokens using standardized utilities
	const getAvailableTokens = (): ContextToken[] => {
		const tokens: ContextToken[] = [];

		// Get standardized variables from upstream nodes
		const standardizedVariables = getStandardizedVariables(nodeId, getNodes(), getEdges());

		// Convert standardized variables to ContextToken format
		standardizedVariables.forEach(variable => {
			// Map source to allowed ContextToken source values
			let mappedSource: 'form' | 'transform' | 'upstream' | 'start';
			if (variable.source === 'form') {
				mappedSource = 'form';
			} else if (variable.source === 'start') {
				mappedSource = 'start';
			} else {
				mappedSource = 'upstream';
			}

			tokens.push({
				id: variable.id,
				label: variable.cleanName,
				type: variable.field.type,
				description: variable.field.description || `From ${variable.context.nodeLabel}`,
				source: mappedSource,
			});
		});

		// Add current transformation outputs (ones that are already defined)
		transformations.forEach(transformation => {
			if (transformation.output && transformation.output.trim()) {
				tokens.push({
					id: transformation.output,
					label: transformation.output,
					type: 'string', // Default type
					description:
						transformation.description ||
						`Transformation output: ${transformation.output}`,
					source: 'transform',
				});
			}
		});

		return tokens;
	};

	const availableTokens = getAvailableTokens();
	const filteredTokens = availableTokens.filter(
		token =>
			token.label.toLowerCase().includes(contextSearchTerm.toLowerCase()) ||
			token.description?.toLowerCase().includes(contextSearchTerm.toLowerCase())
	);

	const insertToken = (
		token: string,
		transformationId: string,
		fieldType: 'expression' | 'condition' | 'output'
	) => {
		const transformation = transformations.find(t => t.id === transformationId);
		if (!transformation) return;

		if (fieldType === 'expression') {
			const newValue = transformation.expression + `{{${token}}}`;
			updateTransformation(transformationId, { expression: newValue });
		} else if (fieldType === 'condition') {
			const currentValue = transformation.condition || '';
			const newValue = currentValue + `{{${token}}}`;
			updateTransformation(transformationId, { condition: newValue });
		} else if (fieldType === 'output') {
			const newValue = transformation.output + `{{${token}}}`;
			updateTransformation(transformationId, { output: newValue });
		}

		setActiveTokenDropdown(null);
	};

	useEffect(() => {
		setShowDefaultValues(config.onError === 'default');
	}, [config.onError]);

	const handleConfigChange = (newConfig: Partial<TransformConfig>) => {
		const updatedConfig = { ...config, ...newConfig };
		setConfig(updatedConfig);
		onChange({
			...data,
			config: updatedConfig,
		});
	};

	const handleTransformationsChange = (newTransformations: Transformation[]) => {
		setTransformations(newTransformations);
		// Ensure each transformation has an ID when saving to config
		const configTransformations = newTransformations.map((t, index) => ({
			...t,
			id: t.id || `transform-${index}-${Date.now()}`,
		}));
		handleConfigChange({ transformations: configTransformations });
	};

	const addTransformation = () => {
		const newTransformation: Transformation = {
			id: `transform-${Date.now()}`,
			output: '',
			expression: '',
			description: '',
		};
		handleTransformationsChange([...transformations, newTransformation]);
	};

	const removeTransformation = (id: string) => {
		if (transformations.length > 1) {
			handleTransformationsChange(transformations.filter(t => t.id !== id));
		}
	};

	// Helper function to format and sanitize output field names
	const formatOutputField = (input: string): string => {
		return input
			.toLowerCase()
			.replace(/[^a-z0-9\s]/g, '') // Remove special characters
			.replace(/\s+/g, '_') // Replace spaces with underscores
			.replace(/_+/g, '_') // Replace multiple underscores with single
			.replace(/^_+|_+$/g, ''); // Remove leading/trailing underscores
	};

	const updateTransformation = (id: string, updates: Partial<Transformation>) => {
		handleTransformationsChange(
			transformations.map(t => (t.id === id ? { ...t, ...updates } : t))
		);
	};

	const handleOutputFieldChange = (id: string, value: string) => {
		const formatted = formatOutputField(value);
		updateTransformation(id, { output: formatted });
	};

	const handleDragEnd = (result: DropResult) => {
		if (!result.destination) return;

		const items = Array.from(transformations);
		const [reorderedItem] = items.splice(result.source.index, 1);
		items.splice(result.destination.index, 0, reorderedItem);

		handleTransformationsChange(items);
	};

	const handleDefaultValueChange = (output: string, value: string) => {
		const newDefaultValues = { ...config.defaultValues };
		if (value.trim()) {
			try {
				// Try to parse as JSON for objects/arrays/numbers/booleans
				newDefaultValues[output] = JSON.parse(value);
			} catch {
				// If JSON parse fails, treat as string
				newDefaultValues[output] = value;
			}
		} else {
			delete newDefaultValues[output];
		}
		handleConfigChange({ defaultValues: newDefaultValues });
	};

	const getDefaultValueForOutput = (output: string): string => {
		const value = config.defaultValues?.[output];
		if (value === undefined) return '';
		return typeof value === 'string' ? value : JSON.stringify(value);
	};

	// Missing functions that are referenced in WizardStep components
	const handleTransformationChange = (id: string, updates: Partial<Transformation>) => {
		updateTransformation(id, updates);
	};

	const handleTransformationDelete = (id: string) => {
		removeTransformation(id);
	};

	const onDragEnd = (result: DropResult) => {
		handleDragEnd(result);
	};

	// TransformationCard Component
	const TransformationCard = ({
		transformation,
		index,
		onChange,
		onDelete,
		onDragEnd,
		activeTokenDropdown,
		setActiveTokenDropdown,
		contextSearchTerm,
		setContextSearchTerm,
		insertToken,
		getAvailableTokens,
	}: {
		transformation: Transformation;
		index: number;
		onChange: (id: string, updates: Partial<Transformation>) => void;
		onDelete: (id: string) => void;
		onDragEnd: (result: DropResult) => void;
		activeTokenDropdown: string | null;
		setActiveTokenDropdown: (id: string | null) => void;
		contextSearchTerm: string;
		setContextSearchTerm: (term: string) => void;
		insertToken: (
			token: string,
			transformationId: string,
			fieldType: 'expression' | 'condition' | 'output'
		) => void;
		getAvailableTokens: () => ContextToken[];
	}) => {
		const availableTokens = getAvailableTokens();
		const filteredTokens = availableTokens.filter(token =>
			token.label.toLowerCase().includes(contextSearchTerm.toLowerCase())
		);

		return (
			<Draggable draggableId={transformation.id} index={index}>
				{(provided, snapshot) => (
					<div
						ref={provided.innerRef}
						{...provided.draggableProps}
						className={`p-4 bg-white rounded-lg border border-gray-200 shadow-sm transition-all dark:bg-gray-800 dark:border-gray-700 ${
							snapshot.isDragging ? 'shadow-lg ring-2 ring-blue-500' : ''
						}`}
					>
						<div className="flex gap-3 items-start">
							<div
								{...provided.dragHandleProps}
								className="flex-shrink-0 w-5 h-5 text-gray-400 cursor-grab active:cursor-grabbing dark:text-gray-500"
							>
								<GripVertical />
							</div>

							<div className="flex-1 space-y-4">
								{/* Output Field */}
								<div className="space-y-2">
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
										Output Field Name
									</label>
									<div className="flex gap-2">
										<input
											type="text"
											value={transformation.output}
											onChange={e =>
												onChange(transformation.id, {
													output: e.target.value,
												})
											}
											placeholder="e.g., fullName, totalPrice, status"
											className="flex-1 px-3 py-2 text-sm text-gray-900 bg-white rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:focus:ring-blue-400"
										/>
										<button
											type="button"
											onClick={() =>
												setActiveTokenDropdown(
													`${transformation.id}-output`
												)
											}
											className="px-2 py-2 text-xs text-blue-600 bg-blue-50 rounded border border-blue-200 hover:bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20 dark:border-blue-700 dark:hover:bg-blue-900/40"
										>
											<Plus className="w-3 h-3" />
										</button>
									</div>
									{activeTokenDropdown === `${transformation.id}-output` && (
										<div className="relative">
											<div className="absolute right-0 top-full z-50 mt-1 w-64 max-h-48 bg-white rounded-md border border-gray-200 shadow-lg dark:bg-gray-700 dark:border-gray-600">
												<div className="p-2 border-b border-gray-200 dark:border-gray-600">
													<div className="relative">
														<Search className="absolute left-2 top-1/2 w-3 h-3 text-gray-400 transform -translate-y-1/2" />
														<input
															type="text"
															value={contextSearchTerm}
															onChange={e =>
																setContextSearchTerm(e.target.value)
															}
															placeholder="Search..."
															className="py-1 pr-2 pl-7 w-full text-xs text-gray-900 bg-gray-50 rounded border border-gray-200 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
														/>
													</div>
												</div>
												<div className="overflow-y-auto max-h-32">
													{filteredTokens.map(token => (
														<button
															key={token.id}
															type="button"
															onClick={() => {
																insertToken(
																	token.label,
																	transformation.id,
																	'output'
																);
																setActiveTokenDropdown(null);
															}}
															className="px-3 py-2 w-full text-xs text-left border-b border-gray-100 hover:bg-gray-100 dark:hover:bg-gray-600 dark:border-gray-600 last:border-b-0"
														>
															<div className="flex justify-between items-center">
																<code className="font-mono text-blue-600 dark:text-blue-400">
																	{`{{${token.label}}}`}
																</code>
																<span className="text-gray-500 dark:text-gray-400">
																	{token.type}
																</span>
															</div>
														</button>
													))}
													{filteredTokens.length === 0 && (
														<div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
															No tokens found
														</div>
													)}
												</div>
											</div>
										</div>
									)}
								</div>

								{/* Expression */}
								<div className="space-y-2">
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
										Transformation Expression
									</label>
									<div className="flex gap-2">
										<textarea
											value={transformation.expression}
											onChange={e =>
												onChange(transformation.id, {
													expression: e.target.value,
												})
											}
											placeholder="e.g., {{firstName}} + ' ' + {{lastName}}, {{price}} * 1.08, {{age}} >= 18 ? 'adult' : 'minor'"
											rows={3}
											className="flex-1 px-3 py-2 text-sm font-mono text-gray-900 bg-white rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:focus:ring-blue-400"
										/>
										<button
											type="button"
											onClick={() =>
												setActiveTokenDropdown(
													`${transformation.id}-expression`
												)
											}
											className="px-2 py-2 text-xs text-blue-600 bg-blue-50 rounded border border-blue-200 hover:bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20 dark:border-blue-700 dark:hover:bg-blue-900/40"
										>
											<Plus className="w-3 h-3" />
										</button>
									</div>
									{activeTokenDropdown === `${transformation.id}-expression` && (
										<div className="relative">
											<div className="absolute right-0 top-full z-50 mt-1 w-64 max-h-48 bg-white rounded-md border border-gray-200 shadow-lg dark:bg-gray-700 dark:border-gray-600">
												<div className="p-2 border-b border-gray-200 dark:border-gray-600">
													<div className="relative">
														<Search className="absolute left-2 top-1/2 w-3 h-3 text-gray-400 transform -translate-y-1/2" />
														<input
															type="text"
															value={contextSearchTerm}
															onChange={e =>
																setContextSearchTerm(e.target.value)
															}
															placeholder="Search..."
															className="py-1 pr-2 pl-7 w-full text-xs text-gray-900 bg-gray-50 rounded border border-gray-200 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
														/>
													</div>
												</div>
												<div className="overflow-y-auto max-h-32">
													{filteredTokens.map(token => (
														<button
															key={token.id}
															type="button"
															onClick={() => {
																insertToken(
																	token.label,
																	transformation.id,
																	'expression'
																);
																setActiveTokenDropdown(null);
															}}
															className="px-3 py-2 w-full text-xs text-left border-b border-gray-100 hover:bg-gray-100 dark:hover:bg-gray-600 dark:border-gray-600 last:border-b-0"
														>
															<div className="flex justify-between items-center">
																<code className="font-mono text-blue-600 dark:text-blue-400">
																	{`{{${token.label}}}`}
																</code>
																<span className="text-gray-500 dark:text-gray-400">
																	{token.type}
																</span>
															</div>
														</button>
													))}
													{filteredTokens.length === 0 && (
														<div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
															No tokens found
														</div>
													)}
												</div>
											</div>
										</div>
									)}
								</div>

								{/* Condition (Optional) */}
								<div className="space-y-2">
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
										Condition (Optional)
									</label>
									<div className="flex gap-2">
										<textarea
											value={transformation.condition || ''}
											onChange={e =>
												onChange(transformation.id, {
													condition: e.target.value,
												})
											}
											placeholder="e.g., {{status}} === 'active', {{amount}} > 0"
											rows={2}
											className="flex-1 px-3 py-2 text-sm font-mono text-gray-900 bg-white rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:focus:ring-blue-400"
										/>
										<button
											type="button"
											onClick={() =>
												setActiveTokenDropdown(
													`${transformation.id}-condition`
												)
											}
											className="px-2 py-2 text-xs text-blue-600 bg-blue-50 rounded border border-blue-200 hover:bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20 dark:border-blue-700 dark:hover:bg-blue-900/40"
										>
											<Plus className="w-3 h-3" />
										</button>
									</div>
									{activeTokenDropdown === `${transformation.id}-condition` && (
										<div className="relative">
											<div className="absolute right-0 top-full z-50 mt-1 w-64 max-h-48 bg-white rounded-md border border-gray-200 shadow-lg dark:bg-gray-700 dark:border-gray-600">
												<div className="p-2 border-b border-gray-200 dark:border-gray-600">
													<div className="relative">
														<Search className="absolute left-2 top-1/2 w-3 h-3 text-gray-400 transform -translate-y-1/2" />
														<input
															type="text"
															value={contextSearchTerm}
															onChange={e =>
																setContextSearchTerm(e.target.value)
															}
															placeholder="Search..."
															className="py-1 pr-2 pl-7 w-full text-xs text-gray-900 bg-gray-50 rounded border border-gray-200 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
														/>
													</div>
												</div>
												<div className="overflow-y-auto max-h-32">
													{filteredTokens.map(token => (
														<button
															key={token.id}
															type="button"
															onClick={() => {
																insertToken(
																	token.label,
																	transformation.id,
																	'condition'
																);
																setActiveTokenDropdown(null);
															}}
															className="px-3 py-2 w-full text-xs text-left border-b border-gray-100 hover:bg-gray-100 dark:hover:bg-gray-600 dark:border-gray-600 last:border-b-0"
														>
															<div className="flex justify-between items-center">
																<code className="font-mono text-blue-600 dark:text-blue-400">
																	{`{{${token.label}}}`}
																</code>
																<span className="text-gray-500 dark:text-gray-400">
																	{token.type}
																</span>
															</div>
														</button>
													))}
													{filteredTokens.length === 0 && (
														<div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
															No tokens found
														</div>
													)}
												</div>
											</div>
										</div>
									)}
									<p className="text-xs text-gray-500 dark:text-gray-400">
										Only apply this transformation when the condition evaluates
										to true
									</p>
								</div>

								{/* Description */}
								<div className="space-y-2">
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
										Description (Optional)
									</label>
									<input
										type="text"
										value={transformation.description || ''}
										onChange={e =>
											onChange(transformation.id, {
												description: e.target.value,
											})
										}
										placeholder="Brief description of what this transformation does"
										className="px-3 py-2 w-full text-sm text-gray-900 bg-white rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:focus:ring-blue-400"
									/>
								</div>
							</div>

							<button
								onClick={() => onDelete(transformation.id)}
								className="flex-shrink-0 p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20"
								title="Delete transformation"
							>
								<Trash2 className="w-4 h-4" />
							</button>
						</div>
					</div>
				)}
			</Draggable>
		);
	};

	// Wizard Step Components
	function WizardStep1({
		config,
		onChange,
		canProceed,
	}: {
		config: TransformConfig;
		onChange: (changes: Partial<TransformConfig>) => void;
		canProceed: boolean;
	}) {
		return (
			<div className="space-y-6">
				<div>
					<h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
						Basic Setup
					</h3>
					<p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
						Configure the basic settings for your transform node.
					</p>
				</div>

				{/* Storage Key Configuration */}
				<div className="space-y-2">
					<label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
						Storage Key (Optional)
					</label>
					<input
						type="text"
						value={config.storeAs || ''}
						onChange={e => onChange({ storeAs: e.target.value })}
						placeholder="userData (defaults to node ID)"
						className="px-4 py-3 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 shadow-sm transition-colors dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-purple-500 dark:focus:border-purple-400 dark:bg-gray-800 dark:text-white"
					/>
					<p className="text-xs text-gray-500 dark:text-gray-400">
						Customize how downstream nodes access transformed data. Leave empty to use
						the node ID.
					</p>
				</div>

				{/* Error Handling */}
				<div className="space-y-2">
					<label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
						On Error Behavior
					</label>
					<select
						value={config.onError}
						onChange={e =>
							onChange({ onError: e.target.value as 'fail' | 'skip' | 'default' })
						}
						className="px-3 py-2 w-full text-gray-900 bg-white rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:ring-blue-400"
					>
						<option value="fail">Fail - Stop execution on any error</option>
						<option value="skip">
							Skip - Continue execution, skip failed transformations
						</option>
						<option value="default">
							Default - Use fallback values for failed transformations
						</option>
					</select>
					<p className="text-xs text-gray-500 dark:text-gray-400">
						Choose how the node should behave when transformations fail.
					</p>
				</div>

				{/* Preserve Original */}
				<div className="space-y-2">
					<label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
						Output Options
					</label>
					<div className="flex items-center">
						<input
							type="checkbox"
							id="preserveOriginal"
							checked={config.preserveOriginal !== false}
							onChange={e => onChange({ preserveOriginal: e.target.checked })}
							className="mr-3 w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
						/>
						<label
							htmlFor="preserveOriginal"
							className="text-sm text-gray-700 dark:text-gray-300"
						>
							Preserve Original Context
							<span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
								(Keep original data alongside transformed results)
							</span>
						</label>
					</div>
				</div>

				{/* Step Validation */}
				{!canProceed && (
					<div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md dark:bg-yellow-900/20 dark:border-yellow-800">
						<p className="text-sm text-yellow-800 dark:text-yellow-200">
							⚠️ You need to add at least one transformation to proceed to the next
							step.
						</p>
					</div>
				)}
			</div>
		);
	}

	function WizardStep2({
		config,
		onChange,
		canProceed,
		transformations,
		setTransformations,
		onDragEnd,
		activeTokenDropdown,
		setActiveTokenDropdown,
		contextSearchTerm,
		setContextSearchTerm,
		insertToken,
		getAvailableTokens,
	}: {
		config: TransformConfig;
		onChange: (changes: Partial<TransformConfig>) => void;
		canProceed: boolean;
		transformations: Transformation[];
		setTransformations: (transformations: Transformation[]) => void;
		onDragEnd: (result: DropResult) => void;
		activeTokenDropdown: string | null;
		setActiveTokenDropdown: (id: string | null) => void;
		contextSearchTerm: string;
		setContextSearchTerm: (term: string) => void;
		insertToken: (
			token: string,
			transformationId: string,
			fieldType: 'output' | 'expression' | 'condition'
		) => void;
		getAvailableTokens: () => ContextToken[];
	}) {
		return (
			<div className="space-y-6">
				<div>
					<h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
						Data Transformations
					</h3>
					<p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
						Define how you want to transform your data. Add at least one transformation
						to proceed.
					</p>
				</div>

				{/* Transformations List */}
				<DragDropContext onDragEnd={onDragEnd}>
					<Droppable droppableId="transformations">
						{provided => (
							<div
								{...provided.droppableProps}
								ref={provided.innerRef}
								className="space-y-4"
							>
								{transformations.map((transformation, index) => (
									<TransformationCard
										key={transformation.id}
										transformation={transformation}
										index={index}
										onChange={handleTransformationChange}
										onDelete={handleTransformationDelete}
										onDragEnd={onDragEnd}
										activeTokenDropdown={activeTokenDropdown}
										setActiveTokenDropdown={setActiveTokenDropdown}
										contextSearchTerm={contextSearchTerm}
										setContextSearchTerm={setContextSearchTerm}
										insertToken={insertToken}
										getAvailableTokens={getAvailableTokens}
									/>
								))}
								{provided.placeholder}
							</div>
						)}
					</Droppable>
				</DragDropContext>

				<button
					onClick={addTransformation}
					className="flex gap-2 items-center px-4 py-2 text-sm text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20 dark:border-blue-700 dark:hover:bg-blue-900/40"
				>
					<Plus className="w-4 h-4" />
					Add Transformation
				</button>

				{/* Step Validation */}
				{!canProceed && (
					<div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md dark:bg-yellow-900/20 dark:border-yellow-800">
						<p className="text-sm text-yellow-800 dark:text-yellow-200">
							⚠️ You need to add at least one expression to proceed to the next step.
						</p>
					</div>
				)}
			</div>
		);
	}

	function WizardStep3({
		config,
		onChange,
		transformations,
		showDefaultValues,
		setShowDefaultValues,
		getDefaultValueForOutput,
		handleDefaultValueChange,
	}: {
		config: TransformConfig;
		onChange: (changes: Partial<TransformConfig>) => void;
		transformations: Transformation[];
		showDefaultValues: boolean;
		setShowDefaultValues: (show: boolean) => void;
		getDefaultValueForOutput: (output: string) => string;
		handleDefaultValueChange: (output: string, value: string) => void;
	}) {
		return (
			<div className="space-y-6">
				<div>
					<h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
						Test & Review
					</h3>
					<p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
						Review your configuration and set up any additional options.
					</p>
				</div>

				{/* Storage Configuration Banner */}
				{config.storeAs && (
					<div className="p-4 bg-purple-50 rounded-lg border border-purple-200 dark:bg-purple-900/20 dark:border-purple-800">
						<div className="flex gap-3 items-start">
							<div className="flex-shrink-0 w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5">
								🔄
							</div>
							<div>
								<h4 className="font-medium text-purple-900 dark:text-purple-100">
									Custom Storage Key
								</h4>
								<p className="mt-1 text-sm text-purple-700 dark:text-purple-300">
									Transformed data will be stored as:{' '}
									<code className="px-1 bg-purple-100 rounded dark:bg-purple-800">
										{'{{' + config.storeAs + '.*}}'}
									</code>
								</p>
							</div>
						</div>
					</div>
				)}

				{/* Transformations Summary */}
				<div className="p-4 bg-gray-50 rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
					<h4 className="mb-3 font-medium text-gray-900 dark:text-white">
						Transformations Summary
					</h4>
					<div className="space-y-2">
						{transformations
							.filter(t => t.output.trim() && t.expression.trim())
							.map(transformation => (
								<div
									key={transformation.id}
									className="flex justify-between items-center p-2 bg-white rounded border dark:bg-gray-700 dark:border-gray-600"
								>
									<div>
										<span className="font-medium text-gray-900 dark:text-white">
											{transformation.output}
										</span>
										<span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
											= {transformation.expression}
										</span>
									</div>
									<span className="text-xs text-green-600 dark:text-green-400 bg-green-50 px-2 py-1 rounded dark:bg-green-900/20">
										✓ Valid
									</span>
								</div>
							))}
						{transformations.filter(t => t.output.trim() && t.expression.trim())
							.length === 0 && (
							<p className="text-sm italic text-gray-500 dark:text-gray-400">
								No valid transformations configured yet.
							</p>
						)}
					</div>
				</div>

				{/* Default Values Section */}
				<div className="space-y-4">
					<div className="flex items-center justify-between">
						<h4 className="font-medium text-gray-900 dark:text-white">
							Default Values (Optional)
						</h4>
						<button
							onClick={() => setShowDefaultValues(!showDefaultValues)}
							className="px-3 py-1 text-sm text-blue-600 bg-blue-50 rounded border border-blue-200 hover:bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20 dark:border-blue-700 dark:hover:bg-blue-900/40"
						>
							{showDefaultValues ? 'Hide' : 'Show'} Default Values
						</button>
					</div>

					{showDefaultValues && (
						<div className="p-4 bg-gray-50 rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
							<p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
								Define fallback values for transformations that fail. Key should
								match the output field name.
							</p>

							{transformations
								.filter(t => t.output.trim())
								.map(transformation => (
									<div key={transformation.id} className="mb-3">
										<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
											Default for "{transformation.output}"
										</label>
										<input
											type="text"
											value={getDefaultValueForOutput(transformation.output)}
											onChange={e =>
												handleDefaultValueChange(
													transformation.output,
													e.target.value
												)
											}
											className="px-3 py-2 w-full font-mono text-gray-900 bg-white rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-600 dark:bg-gray-700 dark:text-white dark:focus:ring-blue-400"
											placeholder="Default value (string, number, true/false, or JSON object)"
										/>
									</div>
								))}

							{transformations.filter(t => t.output.trim()).length === 0 && (
								<p className="text-sm italic text-gray-500 dark:text-gray-400">
									Add output field names to your transformations above to
									configure default values.
								</p>
							)}
						</div>
					)}
				</div>

				{/* Usage Examples */}
				<div className="p-4 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
					<h4 className="mb-2 font-medium text-blue-900 text-md dark:text-blue-100">
						Expression Examples
					</h4>
					<div className="space-y-2 text-sm text-blue-800 dark:text-blue-200">
						<div>
							<strong>String concatenation:</strong>{' '}
							<code>{"{{firstName}} + ' ' + {{lastName}}"}</code>
						</div>
						<div>
							<strong>Math calculations:</strong> <code>{'{{price}} * 1.08'}</code>
						</div>
						<div>
							<strong>Conditional logic:</strong>{' '}
							<code>{"{{age}} >= 18 ? 'adult' : 'minor'"}</code>
						</div>
						<div>
							<strong>Array operations:</strong> <code>{'{{items}}.length'}</code>
						</div>
						<div>
							<strong>Date formatting:</strong> <code>new Date().toISOString()</code>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6 px-6 py-4">
			{/* Validation Results */}
			{(isValidating || lastValidationResult) && (
				<ValidationResultDisplay
					errors={lastValidationResult?.errors || []}
					isValidating={isValidating}
					onDismiss={clearValidationResult}
				/>
			)}

			{/* Wizard Mode - Step-based Configuration */}
			{wizardMode ? (
				<>
					{/* Wizard Header */}
					<div className="mb-6">
						<div className="flex justify-between items-center mb-4">
							<div>
								<h2 className="text-xl font-semibold text-gray-900 dark:text-white">
									Transform Data Configuration
								</h2>
								<p className="text-sm text-gray-600 dark:text-gray-400">
									Step {currentStep} of 3:{' '}
									{currentStep === 1
										? 'Basic Setup'
										: currentStep === 2
										? 'Transformations'
										: 'Test & Review'}
								</p>
								<div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
									{Math.round((currentStep / 3) * 100)}% Complete
								</div>
							</div>
							<button
								onClick={() => setWizardMode(false)}
								className="px-3 py-1 text-sm text-gray-600 bg-gray-100 rounded border border-gray-300 hover:bg-gray-200 dark:text-gray-400 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600"
							>
								Exit Wizard
							</button>
						</div>

						{/* Progress Bar */}
						<div className="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700">
							<div
								className="bg-blue-600 h-2 rounded-full transition-all duration-300"
								style={{ width: `${(currentStep / 3) * 100}%` }}
							/>
						</div>

						{/* Step Indicators */}
						<div className="flex justify-between mt-4">
							{[1, 2, 3].map(step => (
								<div
									key={step}
									className={`flex flex-col items-center ${
										step <= currentStep
											? 'text-blue-600 dark:text-blue-400'
											: 'text-gray-400 dark:text-gray-500'
									}`}
								>
									<div
										className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
											step <= currentStep
												? 'bg-blue-600 text-white'
												: 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
										}`}
									>
										{step < currentStep ? '✓' : step}
									</div>
									<div className="mt-1 text-xs text-center">
										{step === 1 ? 'Setup' : step === 2 ? 'Transform' : 'Test'}
									</div>
								</div>
							))}
						</div>
					</div>

					{/* Step Content */}
					{currentStep === 1 && (
						<WizardStep1
							config={config}
							onChange={handleConfigChange}
							canProceed={canProceedToStep(2)}
						/>
					)}

					{currentStep === 2 && (
						<WizardStep2
							config={config}
							onChange={handleConfigChange}
							canProceed={canProceedToStep(3)}
							transformations={transformations}
							setTransformations={setTransformations}
							onDragEnd={onDragEnd}
							activeTokenDropdown={activeTokenDropdown}
							setActiveTokenDropdown={setActiveTokenDropdown}
							contextSearchTerm={contextSearchTerm}
							setContextSearchTerm={setContextSearchTerm}
							insertToken={insertToken}
							getAvailableTokens={getAvailableTokens}
						/>
					)}

					{currentStep === 3 && (
						<WizardStep3
							config={config}
							onChange={handleConfigChange}
							transformations={transformations}
							showDefaultValues={showDefaultValues}
							setShowDefaultValues={setShowDefaultValues}
							getDefaultValueForOutput={getDefaultValueForOutput}
							handleDefaultValueChange={handleDefaultValueChange}
						/>
					)}
				</>
			) : (
				<>
					{/* Advanced Mode - All Configuration at Once */}
					<div className="mb-6">
						<div className="flex justify-between items-center">
							<h2 className="text-xl font-semibold text-gray-900 dark:text-white">
								Transform Data Configuration
							</h2>
							<button
								onClick={() => setWizardMode(true)}
								className="px-3 py-1 text-sm text-blue-600 bg-blue-50 rounded border border-blue-200 hover:bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20 dark:border-blue-700 dark:hover:bg-blue-900/40"
							>
								Start Wizard
							</button>
						</div>
					</div>

					{/* Storage Configuration Banner */}
					{config.storeAs && (
						<div className="p-4 bg-purple-50 rounded-lg border border-purple-200 dark:bg-purple-900/20 dark:border-purple-800">
							<div className="flex gap-3 items-start">
								<div className="flex-shrink-0 w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5">
									🔄
								</div>
								<div>
									<h4 className="font-medium text-purple-900 dark:text-purple-100">
										Custom Storage Key
									</h4>
									<p className="mt-1 text-sm text-purple-700 dark:text-purple-300">
										Transformed data will be stored as:{' '}
										<code className="px-1 bg-purple-100 rounded dark:bg-purple-800">
											{'{{' + config.storeAs + '.*}}'}
										</code>
									</p>
								</div>
							</div>
						</div>
					)}

					{/* Storage Key Configuration */}
					<div className="space-y-2">
						<label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
							Storage Key (Optional)
						</label>
						<input
							type="text"
							value={config.storeAs || ''}
							onChange={e => handleConfigChange({ storeAs: e.target.value })}
							placeholder="userData (defaults to node ID)"
							className="px-4 py-3 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 shadow-sm transition-colors dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-purple-500 dark:focus:border-purple-400 dark:bg-gray-800 dark:text-white"
						/>
					</div>

					{/* Transformations Section */}
					<div>
						<h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
							Data Transformations
						</h3>
						<DragDropContext onDragEnd={onDragEnd}>
							<Droppable droppableId="transformations-advanced">
								{provided => (
									<div
										{...provided.droppableProps}
										ref={provided.innerRef}
										className="space-y-4"
									>
										{transformations.map((transformation, index) => (
											<TransformationCard
												key={transformation.id}
												transformation={transformation}
												index={index}
												onChange={handleTransformationChange}
												onDelete={handleTransformationDelete}
												onDragEnd={onDragEnd}
												activeTokenDropdown={activeTokenDropdown}
												setActiveTokenDropdown={setActiveTokenDropdown}
												contextSearchTerm={contextSearchTerm}
												setContextSearchTerm={setContextSearchTerm}
												insertToken={insertToken}
												getAvailableTokens={getAvailableTokens}
											/>
										))}
										{provided.placeholder}
									</div>
								)}
							</Droppable>
						</DragDropContext>

						<button
							onClick={addTransformation}
							className="flex gap-2 items-center px-4 py-2 text-sm text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20 dark:border-blue-700 dark:hover:bg-blue-900/40"
						>
							<Plus className="w-4 h-4" />
							Add Transformation
						</button>
					</div>

					{/* Error Handling */}
					<div>
						<h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
							Error Handling
						</h3>
						<div className="space-y-4">
							<div>
								<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
									On Error Behavior
								</label>
								<select
									value={config.onError}
									onChange={e =>
										handleConfigChange({
											onError: e.target.value as 'fail' | 'skip' | 'default',
										})
									}
									className="px-3 py-2 w-full text-gray-900 bg-white rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:ring-blue-400"
								>
									<option value="fail">Fail - Stop execution on any error</option>
									<option value="skip">
										Skip - Continue execution, skip failed transformations
									</option>
									<option value="default">
										Default - Use fallback values for failed transformations
									</option>
								</select>
							</div>

							{/* Default Values Section */}
							{showDefaultValues && (
								<div className="p-4 bg-gray-50 rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
									<h4 className="mb-3 font-medium text-gray-900 text-md dark:text-white">
										Default Values
									</h4>
									<p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
										Define fallback values for transformations that fail. Key
										should match the output field name.
									</p>

									{transformations
										.filter(t => t.output.trim())
										.map(transformation => (
											<div key={transformation.id} className="mb-3">
												<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
													Default for "{transformation.output}"
												</label>
												<input
													type="text"
													value={getDefaultValueForOutput(
														transformation.output
													)}
													onChange={e =>
														handleDefaultValueChange(
															transformation.output,
															e.target.value
														)
													}
													className="px-3 py-2 w-full font-mono text-gray-900 bg-white rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-600 dark:bg-gray-700 dark:text-white dark:focus:ring-blue-400"
													placeholder="Default value (string, number, true/false, or JSON object)"
												/>
											</div>
										))}

									{transformations.filter(t => t.output.trim()).length === 0 && (
										<p className="text-sm italic text-gray-500 dark:text-gray-400">
											Add output field names to your transformations above to
											configure default values.
										</p>
									)}
								</div>
							)}
						</div>
					</div>

					{/* Preserve Original */}
					<div>
						<h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
							Output Options
						</h3>
						<div className="flex items-center">
							<input
								type="checkbox"
								id="preserveOriginal"
								checked={config.preserveOriginal !== false}
								onChange={e =>
									handleConfigChange({ preserveOriginal: e.target.checked })
								}
								className="mr-3 w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
							/>
							<label
								htmlFor="preserveOriginal"
								className="text-sm text-gray-700 dark:text-gray-300"
							>
								Preserve Original Context
								<span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
									(Keep original data alongside transformed results)
								</span>
							</label>
						</div>
					</div>

					{/* Usage Examples */}
					<div className="p-4 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
						<h4 className="mb-2 font-medium text-blue-900 text-md dark:text-blue-100">
							Expression Examples
						</h4>
						<div className="space-y-2 text-sm text-blue-800 dark:text-blue-200">
							<div>
								<strong>String concatenation:</strong>{' '}
								<code>{"{{firstName}} + ' ' + {{lastName}}"}</code>
							</div>
							<div>
								<strong>Math calculations:</strong>{' '}
								<code>{'{{price}} * 1.08'}</code>
							</div>
							<div>
								<strong>Conditional logic:</strong>{' '}
								<code>{"{{age}} >= 18 ? 'adult' : 'minor'"}</code>
							</div>
							<div>
								<strong>Array operations:</strong> <code>{'{{items}}.length'}</code>
							</div>
							<div>
								<strong>Date formatting:</strong>{' '}
								<code>new Date().toISOString()</code>
							</div>
						</div>
					</div>
				</>
			)}
		</div>
	);
}
